// 빡침 곡선 (§6 확정 스펙).
//   - N=20 리샘플 → 항상 20포인트
//   - 세션 자기 피크 정규화
//   - 이동평균 스무딩
//   - 짧은 입력 폴백

import type { AngerCurve, RawSignals } from "./types";

export const BUCKET_COUNT = 20;

// 짧은 입력 폴백 임계 (§6 확정).
const MIN_DURATION_MS = 3000;
const MIN_TOKENS = 2;
const MIN_EVENTS = 10;

/** 의미 토큰 수 대략 추정(공백 분할). */
function tokenCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** 폴백 발동 여부. */
export function isTooShort(s: RawSignals): boolean {
  const isVoice = (s.mode === "voice" || s.mode === "mixed") && !!s.voice;
  const events = isVoice
    ? (s.voice?.volumeTimeline.length ?? 0)
    : s.eventTimestamps.length;
  return (
    s.durationMs < MIN_DURATION_MS ||
    tokenCount(s.text) < MIN_TOKENS ||
    events < MIN_EVENTS
  );
}

/** 활동 타임라인 → 20개 버킷 활동량(정규화 전). */
function bucketize(timestamps: number[], durationMs: number): number[] {
  const buckets = new Array(BUCKET_COUNT).fill(0);
  if (durationMs <= 0) return buckets;
  for (const t of timestamps) {
    const idx = Math.min(
      BUCKET_COUNT - 1,
      Math.floor((t / durationMs) * BUCKET_COUNT),
    );
    if (idx >= 0) buckets[idx] += 1;
  }
  return buckets;
}

/** 음성: 음량 타임라인을 20개로 리샘플. */
function resampleVolume(timeline: number[]): number[] {
  const buckets = new Array(BUCKET_COUNT).fill(0);
  if (timeline.length === 0) return buckets;
  for (let i = 0; i < BUCKET_COUNT; i++) {
    const start = Math.floor((i / BUCKET_COUNT) * timeline.length);
    const end = Math.max(
      start + 1,
      Math.floor(((i + 1) / BUCKET_COUNT) * timeline.length),
    );
    let sum = 0;
    for (let j = start; j < end; j++) sum += timeline[j] ?? 0;
    buckets[i] = sum / (end - start);
  }
  return buckets;
}

/** 가벼운 이동평균 스무딩(윈도 3). */
function smooth(values: number[]): number[] {
  return values.map((_, i) => {
    const a = values[i - 1] ?? values[i];
    const b = values[i];
    const c = values[i + 1] ?? values[i];
    return (a + b + c) / 3;
  });
}

/** 세션 자기 피크로 0~1 정규화(곡선이 항상 높이 꽉 차게). */
function peakNormalize(values: number[]): number[] {
  const peak = Math.max(...values, 0);
  if (peak <= 0) return values.map(() => 0);
  return values.map((v) => v / peak);
}

/** 빡침 곡선 계산. */
export function computeCurve(s: RawSignals): AngerCurve {
  if (isTooShort(s)) {
    // 폴백: 평평한 선.
    return { points: new Array(BUCKET_COUNT).fill(0.15), tooShort: true, peakIndex: 0 };
  }
  const isVoice = (s.mode === "voice" || s.mode === "mixed") && !!s.voice;
  const raw = isVoice
    ? resampleVolume(s.voice?.volumeTimeline ?? [])
    : bucketize(s.eventTimestamps, s.durationMs);
  const points = peakNormalize(smooth(raw));
  let peakIndex = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i] > points[peakIndex]) peakIndex = i;
  }
  return { points, tooShort: false, peakIndex };
}
